import React, { useCallback, useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  UserCheck,
  Search,
  Plus,
  Edit,
  Trash2,
  History,
  RotateCcw,
  XCircle,
  Clock,
  BarChart2,
} from "lucide-react";
import { format } from "date-fns";

import Loader from "../../../loader/Loader";
import AddApprover from "./AddApprover";
import AuditInfoModal, { buildAuditRows } from "../../../shared/AuditInfoModal";
import Pagination from "../../Pagination";
import { domain } from "../../../../security";
import { selectUserID } from "../../../../redux/IchthusSlice";

const formatDate = (dateString) => {
  if (!dateString) return "-";
  return format(new Date(dateString), "MMM dd, yyyy");
};

// ==========================================
// HISTORY MODAL
// ==========================================
const DeletedApproversModal = ({ isOpen, onClose, onRestore }) => {
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [auditItem, setAuditItem] = useState(null);

  const fetchHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${domain}/api/Approvers/history`);
      setHistoryData(response.data);
    } catch {
      toast.error("Failed to load approver history.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchHistory();
      setCurrentPage(1);
    }
  }, [isOpen, fetchHistory]);

  if (!isOpen) return null;

  const filtered = historyData.filter(
    (item) =>
      (item.employeeName || "").toLowerCase().includes(search.toLowerCase()) ||
      (item.userName || "").toLowerCase().includes(search.toLowerCase()),
  );

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filtered.slice(indexOfFirst, indexOfLast);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex justify-between items-center">
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <div className="p-2 bg-red-50 rounded-full text-red-500">
              <Trash2 size={18} />
            </div>
            Deleted Approvers History
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-full transition"
          >
            <XCircle size={22} />
          </button>
        </div>

        <div className="px-4 py-3 bg-slate-50/50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              type="text"
              placeholder="Search deleted approvers..."
              className="w-full pl-9 pr-4 py-2 bg-white border border-slate-200 rounded-xl focus:ring-2 focus:ring-red-100 focus:border-red-400 text-sm outline-none transition-all"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
          {loading ? (
            <div className="flex justify-center py-10">
              <Loader />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-slate-100 mb-3">
                <History size={20} className="text-slate-400" />
              </div>
              <p className="text-slate-500 text-sm">
                No deleted approvers found.
              </p>
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden sm:block bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <table className="w-full text-sm text-left text-slate-600">
                  <thead className="text-xs text-slate-500 uppercase bg-slate-50">
                    <tr>
                      <th className="px-4 py-3">Employee</th>
                      <th className="px-4 py-3">Username</th>
                      <th className="px-4 py-3">Deleted By</th>
                      <th className="px-4 py-3">Deleted Date</th>
                      <th className="px-4 py-3 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {currentItems.map((item) => (
                      <tr key={item.id} className="hover:bg-slate-50">
                        {/* 🔴 FIXED: Changed item.approverId to item.id */}
                        <td className="px-4 py-3 font-medium text-slate-800">
                          <div className="flex items-center gap-1">
                            {item.employeeName}
                            <button
                              onClick={() => setAuditItem(item)}
                              className="text-amber-500 hover:text-amber-700 ml-1"
                              title="View Audit Info"
                            >
                              <Clock size={14} />
                            </button>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-500">
                          @{item.userName}
                        </td>
                        <td className="px-4 py-3 text-xs font-medium text-red-500">
                          {item.deletedByFullName || "Unknown"}
                        </td>
                        <td className="px-4 py-3 text-xs text-red-400">
                          {formatDate(item.deletedDate)}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <button
                            onClick={async () => {
                              await onRestore(item.id); // 🔴 FIXED: Changed item.approverId to item.id
                              fetchHistory();
                            }}
                            className="text-green-600 hover:text-green-700 font-semibold text-xs flex items-center gap-1 ml-auto"
                          >
                            <RotateCcw size={14} /> Restore
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile */}
              <div className="sm:hidden flex flex-col gap-3">
                {currentItems.map((item) => (
                  <div
                    key={item.id} // 🔴 FIXED: Changed item.approverId to item.id
                    className="bg-white p-4 rounded-xl border border-slate-100 shadow-sm"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <span className="font-semibold text-slate-800 block">
                          {item.employeeName}
                        </span>
                        <span className="text-xs text-slate-400">
                          @{item.userName}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setAuditItem(item)}
                          className="p-2 bg-amber-50 text-amber-500 rounded-lg hover:bg-amber-100"
                        >
                          <Clock size={14} />
                        </button>
                        <button
                          onClick={async () => {
                            await onRestore(item.id); // 🔴 FIXED: Changed item.approverId to item.id
                            fetchHistory();
                          }}
                          className="p-2 bg-green-50 text-green-600 rounded-lg hover:bg-green-100"
                        >
                          <RotateCcw size={16} />
                        </button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500">
                      Deleted by:{" "}
                      <span className="font-medium text-red-500">
                        {item.deletedByFullName || "Unknown"}
                      </span>{" "}
                      on {formatDate(item.deletedDate)}
                    </p>
                  </div>
                ))}
              </div>

              <div className="mt-3">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filtered.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.employeeName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

// ==========================================
// MAIN COMPONENT
// ==========================================
const AllApprovers = () => {
  const currentUserID = useSelector(selectUserID);

  const [approverData, setApproverData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approverToEdit, setApproverToEdit] = useState(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isHistoryModalVisible, setIsHistoryModalVisible] = useState(false);
  const [auditItem, setAuditItem] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredApprovers, setFilteredApprovers] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${domain}/api/Approvers`);
      setApproverData(response.data);
    } catch {
      toast.error("Failed to fetch Approvers.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const lower = searchTerm.toLowerCase();
    setFilteredApprovers(
      approverData.filter(
        (item) =>
          (item.employeeName || "").toLowerCase().includes(lower) ||
          (item.userName || "").toLowerCase().includes(lower),
      ),
    );
    setCurrentPage(1);
  }, [searchTerm, approverData]);

  // 🔴 FIXED: Ensure this function receives the ID
  const handleSoftDelete = async (id) => {
    if (!id) {
      toast.error("Error: ID is missing.");
      return;
    }
    if (!window.confirm("Are you sure you want to remove this approver?"))
      return;
    try {
      await axios.put(
        `${domain}/api/Approvers/soft-delete/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Approver moved to history!");
      fetchData();
    } catch (error) {
      console.error(error);
      toast.error("Failed to remove approver.");
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(
        `${domain}/api/Approvers/restore/${id}`,
        { userID: currentUserID },
        { headers: { "Content-Type": "application/json" } },
      );
      toast.success("Approver restored successfully!");
      fetchData();
    } catch {
      toast.error("Failed to restore approver.");
    }
  };

  const indexOfLast = currentPage * itemsPerPage;
  const indexOfFirst = indexOfLast - itemsPerPage;
  const currentItems = filteredApprovers.slice(indexOfFirst, indexOfLast);

  return (
    <div className="min-h-screen pb-12">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-20">
        <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                <UserCheck className="text-indigo-600" /> Approver Management
              </h1>
              <p className="text-sm text-slate-500 mt-1 hidden sm:block">
                Manage authorized approvers for the system.
              </p>
            </div>
            <div className="flex gap-3 w-full md:w-auto">
              <button
                onClick={() => setIsHistoryModalVisible(true)}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-slate-50 border border-slate-200 text-slate-600 hover:bg-slate-100 text-sm font-medium px-4 py-2.5 rounded-xl transition-all"
              >
                <History size={18} /> History
              </button>
              <button
                onClick={() => {
                  setApproverToEdit(null);
                  setIsModalVisible(true);
                }}
                className="flex-1 md:flex-none flex justify-center items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl shadow-md shadow-indigo-200 transition-all"
              >
                <Plus size={18} /> Add Approver
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[95%] mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-4">
            <div className="p-3 bg-indigo-50 text-indigo-600 rounded-xl">
              <BarChart2 size={24} />
            </div>
            <div>
              <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">
                Active Approvers
              </p>
              <p className="text-2xl font-bold text-slate-800">
                {filteredApprovers.length}
              </p>
            </div>
          </div>
          <div className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by name or username..."
                className="w-full pl-10 pr-4 py-2 text-sm border-none ring-1 ring-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none transition-all placeholder:text-slate-400"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-20">
            <Loader />
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden md:block bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden">
              <table className="w-full text-sm text-left text-slate-600">
                <thead className="text-xs text-slate-500 uppercase bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-6 py-4 font-semibold">Employee Name</th>
                    <th className="px-6 py-4 font-semibold">Username</th>
                    <th className="px-6 py-4 font-semibold">Role</th>
                    <th className="px-6 py-4 font-semibold">Department</th>
                    <th className="px-6 py-4 font-semibold">Location</th>
                    <th className="px-6 py-4 font-semibold text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {currentItems.map((item) => (
                    <tr
                      key={item.id} // 🔴 FIXED: Changed item.approverId to item.id
                      className="hover:bg-slate-50 transition"
                    >
                      <td className="px-6 py-4 font-semibold text-slate-800">
                        {item.employeeName}
                      </td>
                      <td className="px-6 py-4 text-slate-500">
                        @{item.userName}
                      </td>
                      <td className="px-6 py-4">
                        <span className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md text-xs font-medium border border-indigo-100">
                          {item.jobRole}
                        </span>
                      </td>
                      <td className="px-6 py-4">{item.department || "-"}</td>
                      <td className="px-6 py-4">{item.location || "-"}</td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => setAuditItem(item)}
                            className="p-2 text-slate-400 hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
                            title="View Audit Info"
                          >
                            <Clock size={18} />
                          </button>
                          <button
                            onClick={() => {
                              setApproverToEdit(item);
                              setIsModalVisible(true);
                            }}
                            className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Edit Approver"
                          >
                            <Edit size={18} />
                          </button>
                          <button
                            onClick={() => handleSoftDelete(item.id)} // 🔴 FIXED: Changed item.approverId to item.id
                            className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Remove Approver"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {currentItems.length === 0 && (
                    <tr>
                      <td
                        colSpan="6"
                        className="px-6 py-10 text-center text-slate-400"
                      >
                        No approvers found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            <div className="p-4 border-t border-slate-100">
              <Pagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredApprovers.length}
                currentPage={currentPage}
                paginate={(p) => setCurrentPage(p)}
              />
            </div>

            {/* Mobile Cards */}
            <div className="md:hidden flex flex-col gap-3">
              {currentItems.map((item) => (
                <div
                  key={item.id} // 🔴 FIXED: Changed item.approverId to item.id
                  className="bg-white rounded-2xl p-4 shadow-sm border border-slate-100 flex flex-col gap-3"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 bg-indigo-50 rounded-lg text-indigo-600">
                        <UserCheck size={16} />
                      </div>
                      <div>
                        <span className="block font-bold text-slate-800">
                          {item.employeeName}
                        </span>
                        <span className="text-xs text-slate-400">
                          @{item.userName}
                        </span>
                      </div>
                    </div>
                    <span className="bg-indigo-50 text-indigo-700 px-2 py-1 rounded-md text-[10px] font-bold border border-indigo-100">
                      {item.jobRole}
                    </span>
                  </div>

                  <div className="text-xs text-slate-500 space-y-1">
                    <p>
                      <span className="font-medium text-slate-600">Dept:</span>{" "}
                      {item.department || "N/A"}
                    </p>
                    <p>
                      <span className="font-medium text-slate-600">
                        Location:
                      </span>{" "}
                      {item.location || "N/A"}
                    </p>
                  </div>

                  <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-50">
                    <button
                      onClick={() => setAuditItem(item)}
                      className="text-xs font-semibold text-amber-500 hover:text-amber-700 flex items-center gap-1"
                    >
                      <Clock size={14} /> Audit
                    </button>
                    <button
                      onClick={() => {
                        setApproverToEdit(item);
                        setIsModalVisible(true);
                      }}
                      className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                    >
                      <Edit size={14} /> Edit
                    </button>
                    <button
                      onClick={() => handleSoftDelete(item.id)} // 🔴 FIXED: Changed item.approverId to item.id
                      className="text-xs font-semibold text-red-500 hover:text-red-700 flex items-center gap-1"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              ))}
              {currentItems.length === 0 && (
                <div className="text-center py-10 text-slate-400 bg-white rounded-lg border border-slate-200">
                  No approvers found.
                </div>
              )}
              <div className="mt-2 pb-6">
                <Pagination
                  itemsPerPage={itemsPerPage}
                  totalItems={filteredApprovers.length}
                  currentPage={currentPage}
                  paginate={(p) => setCurrentPage(p)}
                />
              </div>
            </div>
          </>
        )}
      </div>

      {isModalVisible && (
        <div className="fixed inset-0 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <AddApprover
              onClose={() => {
                setIsModalVisible(false);
                fetchData();
              }}
              refreshData={fetchData}
              approverToEdit={approverToEdit}
            />
          </div>
        </div>
      )}

      <DeletedApproversModal
        isOpen={isHistoryModalVisible}
        onClose={() => {
          setIsHistoryModalVisible(false);
          fetchData();
        }}
        onRestore={handleRestore}
      />

      <AuditInfoModal
        isOpen={!!auditItem}
        onClose={() => setAuditItem(null)}
        title={auditItem?.employeeName}
        rows={buildAuditRows(auditItem)}
      />
    </div>
  );
};

export default AllApprovers;
